<template>
  <div class="flex flex-col product-history-tab" :class="{ 'is-compact': compact }">
    <div class="history-toolbar">
      <input v-model="search_val" type="text" class="border-slate-200 rounded border px-1 text-14px w-48 my-2" placeholder="Kerko" />
      <button
        type="button"
        class="button is-small is-light"
        :disabled="exporting || loading || !guid"
        @click="exportExcel"
      >
        <i class="fa mr-1" :class="exporting ? 'fa-spinner fa-spin' : 'fa-file-excel'"></i>
        Eksporto Excel
      </button>
    </div>
    <div v-if="loading" class="py-4 text-14px has-text-grey">Duke ngarkuar historikun...</div>
    <div v-else-if="!filteredItems.length" class="py-4 text-14px has-text-grey">Nuk ka veprime te regjistruara per kete produkt.</div>
    <div v-else class="overflow-x-auto">
      <table class="table is-narrow is-fullwidth">
        <thead>
          <tr>
            <th></th>
            <th>Kush</th>
            <th>Veprimi</th>
            <th>Ku</th>
            <th>Kur</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in pagedItems" :key="row.id">
            <tr class="text-14px" :class="{ 'is-expanded': expandedId === row.id }">
              <td style="width: 36px">
                <button
                  v-if="hasDetails(row)"
                  type="button"
                  class="button is-small is-light"
                  @click="expandedId = expandedId === row.id ? null : row.id"
                >
                  {{ expandedId === row.id ? "−" : "+" }}
                </button>
              </td>
              <td>{{ row.actor_name || "—" }}</td>
              <td>
                <div>{{ row.action_label || row.action }}</div>
                <div v-if="row.source_label" class="has-text-grey history-desc">{{ row.source_label }}</div>
              </td>
              <td class="audit-ku-cell">
                <div v-if="changeEntries(row).length">
                  <div v-for="(entry, idx) in changeEntries(row)" :key="idx" class="audit-change-line">
                    <span class="has-text-weight-semibold">{{ entry.label }}</span>
                    : {{ entry.old }} → {{ entry.new }}
                  </div>
                </div>
                <div v-else class="has-text-grey">{{ row.source_label || "—" }}</div>
              </td>
              <td>{{ formatDateTime(row.created_at) }}</td>
            </tr>
            <tr v-if="expandedId === row.id && hasDetails(row)" class="audit-detail-row">
              <td colspan="5">
                <div class="audit-detail-card">
                  <div v-if="changeEntries(row).length" class="mb-2">
                    <div class="has-text-weight-semibold mb-1" style="font-size: 12px">Ndryshimet</div>
                    <table class="table is-narrow is-fullwidth" style="font-size: 12px">
                      <thead>
                        <tr>
                          <th>Fusha</th>
                          <th>Para</th>
                          <th>Mbas</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(entry, idx) in changeEntries(row)" :key="idx">
                          <td>{{ entry.label }}</td>
                          <td>{{ entry.old }}</td>
                          <td>{{ entry.new }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="row.meta && row.meta.ok === true" class="has-text-success" style="font-size: 12px">U ruajt në Financa 1 dhe Financa 2.</div>
                  <div v-if="row.meta && row.meta.ok === false" class="has-text-danger" style="font-size: 12px">{{ financaFailText(row) }}</div>
                  <ul v-if="row.meta && Array.isArray(row.meta.failed) && row.meta.failed.length" class="mt-1" style="font-size: 12px">
                    <li v-for="(failure, idx) in row.meta.failed" :key="idx">
                      {{ financaLabel(failure.erp) }}
                      <span v-if="failure.method"> · {{ failure.method }}</span>
                      <span v-if="failure.status != null"> · HTTP {{ failure.status }}</span>
                      <span v-if="failure.reason"> — {{ failure.reason }}</span>
                    </li>
                  </ul>
                  <div v-else-if="row.meta && row.meta.reason" class="has-text-grey" style="font-size: 12px">Arsyeja: {{ row.meta.reason }}</div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div class="flex justify-center items-center mt-4 space-x-2 text-14px">
        <button @click="prevPage" :disabled="current_page === 1" v-show="current_page > 1" class="px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50">
          <i class="fa-solid fa-angle-left"></i>
        </button>
        <span>{{ current_page }}</span>
        <button
          @click.prevent="nextPage"
          :disabled="current_page === totalPages"
          v-show="current_page < totalPages"
          class="px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
        >
          <i class="fa-solid fa-angle-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AuditServices from "@/services/Audit";
import { financaServerLabel, financaTargetsFailureText } from "@/utils/financaSaveNotice";

export default {
  props: {
    guid: { default: null },
    reloadKey: { default: 0 },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      items: [],
      loading: false,
      exporting: false,
      search_val: "",
      current_page: 1,
      items_per_page: 8,
      expandedId: null,
    };
  },
  watch: {
    guid: {
      immediate: true,
      handler() {
        this.load();
      },
    },
    reloadKey() {
      this.load();
    },
    search_val() {
      this.current_page = 1;
    },
  },
  computed: {
    filteredItems() {
      const q = (this.search_val || "").toLowerCase().trim();
      if (!q) return this.items;
      return this.items.filter((row) => JSON.stringify(row).toLowerCase().includes(q));
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.items_per_page));
    },
    pagedItems() {
      const start = (this.current_page - 1) * this.items_per_page;
      return this.filteredItems.slice(start, start + this.items_per_page);
    },
  },
  methods: {
    financaLabel(erp) {
      return financaServerLabel(erp);
    },
    financaFailText(row) {
      const failed = row?.meta?.failed;
      if (Array.isArray(failed) && failed.length) return financaTargetsFailureText(failed);
      return "Nuk u ruajt në Financa.";
    },
    async load() {
      if (!this.guid) {
        this.items = [];
        return;
      }
      this.loading = true;
      this.current_page = 1;
      this.expandedId = null;
      try {
        const res = await AuditServices.forSubject({
          subject_type: "product",
          subject_id: this.guid,
          page: 1,
          per_page: 500,
        });
        this.items = Array.isArray(res?.items) ? res.items : [];
      } catch (e) {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    async exportExcel() {
      if (!this.guid || this.exporting) return;
      this.exporting = true;
      try {
        await AuditServices.exportForSubject({
          subject_type: "product",
          subject_id: this.guid,
        });
        if (this.$toast) this.$toast.success("Historiku u shkarkua.");
      } catch (e) {
        const data = e?.response?.data;
        let message = e?.message || "Nuk u shkarkua eksporti i historikut.";
        if (data instanceof Blob) {
          try {
            const parsed = JSON.parse(await data.text());
            if (parsed?.message) message = parsed.message;
          } catch (err) {
            /* keep message */
          }
        } else if (data?.message) {
          message = data.message;
        }
        if (this.$toast) this.$toast.error(message);
      } finally {
        this.exporting = false;
      }
    },
    changeEntries(row) {
      if (Array.isArray(row?.changes_list) && row.changes_list.length) {
        return row.changes_list;
      }
      const changes = row?.changes;
      if (!changes || typeof changes !== "object") return [];
      return Object.keys(changes).map((field) => {
        const change = changes[field] || {};
        const label = change.label || field;
        const oldVal = this.formatValue(change.old);
        const newVal = this.formatValue(change.new);
        return {
          field,
          label,
          old: oldVal,
          new: newVal,
          text: `${label}: ${oldVal} → ${newVal}`,
        };
      });
    },
    hasDetails(row) {
      const hasChanges = this.changeEntries(row).length > 0;
      const meta = row.meta || {};
      const hasMeta =
        !!meta.reason ||
        meta.ok === true ||
        meta.ok === false ||
        (Array.isArray(meta.failed) && meta.failed.length > 0);
      return hasChanges || hasMeta;
    },
    formatValue(value) {
      if (value === null || value === undefined || value === "") return "—";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    formatDateTime(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return value;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    prevPage() {
      if (this.current_page > 1) this.current_page -= 1;
    },
    nextPage() {
      if (this.current_page < this.totalPages) this.current_page += 1;
    },
  },
};
</script>

<style scoped>
.history-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.history-desc {
  font-size: 12px;
}
.audit-ku-cell {
  min-width: 140px;
}
.audit-change-line {
  font-size: 12px;
  line-height: 1.35;
  word-break: break-word;
}
.product-history-tab.is-compact .table {
  font-size: 12px;
}
.product-history-tab.is-compact th,
.product-history-tab.is-compact td {
  white-space: normal;
  word-break: break-word;
}
.product-history-tab.is-compact .history-desc {
  display: none;
}
.audit-detail-card {
  background: #f8fafc;
  padding: 8px;
  border-radius: 4px;
}
</style>
